import { SYSTEM_ASSIGNMENT_DEFAULTS } from 'utils/assignmentDefaults';
import { getQuizFeedbackDefaults } from 'utils/quizFeedback';
import {
    getImportedQuizRubric,
    getQuizCriterionMapping,
    QuizQuestionMode,
    QuizRubricType,
    reconcileQuizCriterionMapping,
    remapQuizCriterionMapping,
} from 'utils/quizRubric';

export const normalizeQuizQuestion = (question, index = 0) => {
    const aiGradable = Boolean(question?.ai_gradable);
    return {
        id: question?.id ?? index,
        position: Number.isFinite(question?.position) ? question.position : index + 1,
        points_possible: question?.points_possible,
        ai_gradable: aiGradable,
        grading_notes: typeof question?.grading_notes === 'string' ? question.grading_notes : '',
        title: question?.title || '',
        criterion_ids: aiGradable && Array.isArray(question?.criterion_ids) ? [...question.criterion_ids] : [],
    };
};

export const normalizeQuizQuestions = (questions) =>
    (Array.isArray(questions) ? questions : []).filter(Boolean).map(normalizeQuizQuestion);

const getCriterionMapping = (questions) =>
    Object.fromEntries(
        questions.flatMap(({ id, criterion_ids }) => (criterion_ids.length ? [[id, criterion_ids]] : []))
    );

const applyCriterionMapping = (questions, mapping) =>
    questions.map((question) => ({
        ...question,
        criterion_ids: question.ai_gradable ? [...(mapping[question.id] || [])] : [],
    }));

const getOrderedGradableQuestions = (questions) =>
    questions.filter(({ ai_gradable }) => ai_gradable).sort((first, second) => first.position - second.position);

export const mapQuizCriteriaByOrder = (questions, rubric, enabled) =>
    applyCriterionMapping(questions, getQuizCriterionMapping(getOrderedGradableQuestions(questions), rubric, enabled));

export const createQuizDraft = (quiz, courseId) => {
    const sourceQuiz = quiz || {};
    const { questions, ...quizMetadata } = sourceQuiz;
    const feedbackDefaults = getQuizFeedbackDefaults();
    const feedbackSettings = sourceQuiz.ai_feedback_settings;
    const rubric = getImportedQuizRubric(sourceQuiz);
    const isLmsQuiz = Boolean(sourceQuiz.assignment || sourceQuiz.api_assignment_id != null);
    const mapCriteriaByOrder = sourceQuiz.map_criteria_by_order ?? isLmsQuiz;
    const normalizedQuestions = normalizeQuizQuestions(questions);
    return {
        quiz: quiz ? quizMetadata : null,
        questions: mapCriteriaByOrder ? mapQuizCriteriaByOrder(normalizedQuestions, rubric, true) : normalizedQuestions,
        step: 1,
        form: {
            ...feedbackDefaults,
            aiFeedbackEnabled: sourceQuiz.ai_feedback_enabled ?? feedbackDefaults.aiFeedbackEnabled,
            feedbackSettings: {
                ...feedbackDefaults.feedbackSettings,
                ...(feedbackSettings && {
                    structure: feedbackSettings.structure ?? feedbackDefaults.feedbackSettings.structure,
                    numSuggestions:
                        feedbackSettings.num_suggestions ?? feedbackDefaults.feedbackSettings.numSuggestions,
                    suggestionLength:
                        feedbackSettings.suggestion_length ?? feedbackDefaults.feedbackSettings.suggestionLength,
                    languageLevel: feedbackSettings.language_level ?? feedbackDefaults.feedbackSettings.languageLevel,
                    tone: feedbackSettings.tone ?? feedbackDefaults.feedbackSettings.tone,
                    templated: feedbackSettings.templated ?? feedbackDefaults.feedbackSettings.templated,
                    customInstructions:
                        feedbackSettings.custom_instructions ?? feedbackDefaults.feedbackSettings.customInstructions,
                    feedbackExamples:
                        feedbackSettings.feedback_examples ?? feedbackDefaults.feedbackSettings.feedbackExamples,
                }),
            },
            feedbackCustomContext: sourceQuiz.ai_feedback_context ?? feedbackDefaults.feedbackCustomContext,
            aiGradingEnabled: sourceQuiz.ai_grading_enabled ?? SYSTEM_ASSIGNMENT_DEFAULTS.aiGradingEnabled,
            rubric,
            rubricType: sourceQuiz.rubric_type ?? QuizRubricType.HOLISTIC,
            questionMode: sourceQuiz.question_mode ?? QuizQuestionMode.INTERDEPENDENT,
            mapCriteriaByOrder,
            api_assignment_id: sourceQuiz.assignment?.id ?? sourceQuiz.api_assignment_id ?? sourceQuiz.id,
            api_data: {
                ...(sourceQuiz.assignment ?? sourceQuiz.api_data),
                course_id: sourceQuiz.assignment?.course_id ?? sourceQuiz.api_data?.course_id ?? courseId,
            },
        },
        rubricNeedsSync: false,
    };
};

export const reconcileQuestionCriteria = (questions, rubric) =>
    applyCriterionMapping(questions, reconcileQuizCriterionMapping(getCriterionMapping(questions), rubric));

export const remapQuestionCriteria = (questions, previousRubric, rubric) =>
    applyCriterionMapping(questions, remapQuizCriterionMapping(getCriterionMapping(questions), previousRubric, rubric));

export const updateQuizQuestion = (questions, questionId, changes) => {
    const mappedCriterionIds = new Set(
        questions.filter(({ id }) => id !== questionId).flatMap(({ criterion_ids }) => criterion_ids)
    );
    return questions.map((question) =>
        question.id === questionId
            ? {
                  ...question,
                  ...changes,
                  ...(changes.criterion_ids !== undefined && {
                      criterion_ids: Array.isArray(changes.criterion_ids)
                          ? [...new Set(changes.criterion_ids)].filter(
                                (id) => !mappedCriterionIds.has(id) || question.criterion_ids.includes(id)
                            )
                          : [],
                  }),
              }
            : question
    );
};

export const mergeResyncedQuiz = (draft, syncedQuiz, courseId) => {
    const { questions: syncedQuestions, ...syncedMetadata } = syncedQuiz;
    const criterionIdsByQuestionId = new Map(draft.questions.map(({ id, criterion_ids }) => [id, criterion_ids]));
    let questions = normalizeQuizQuestions(syncedQuestions).map((question) => ({
        ...question,
        criterion_ids: question.ai_gradable ? [...(criterionIdsByQuestionId.get(question.id) || [])] : [],
    }));
    if (draft.form.mapCriteriaByOrder) {
        questions = mapQuizCriteriaByOrder(questions, draft.form.rubric, true);
    }

    return {
        ...draft,
        quiz: { ...draft.quiz, ...syncedMetadata },
        questions,
        form: {
            ...draft.form,
            api_assignment_id:
                syncedQuiz.assignment?.id ?? syncedQuiz.api_assignment_id ?? draft.form.api_assignment_id,
            api_data: {
                ...(syncedQuiz.assignment ?? syncedQuiz.api_data ?? draft.form.api_data),
                course_id:
                    syncedQuiz.assignment?.course_id ??
                    syncedQuiz.api_data?.course_id ??
                    draft.form.api_data?.course_id ??
                    courseId,
            },
        },
    };
};

export const hasCompleteQuestionCriteria = (questions, rubricType) =>
    rubricType !== QuizRubricType.QUESTION ||
    questions.filter(({ ai_gradable }) => ai_gradable).every(({ criterion_ids }) => criterion_ids.length > 0);

export const hasUniqueQuestionCriteria = (questions, rubricType) => {
    if (rubricType !== QuizRubricType.QUESTION) return true;
    const mappedCriterionIds = new Set();
    return questions.every(({ criterion_ids }) =>
        [...new Set(criterion_ids)].every((id) => {
            if (mappedCriterionIds.has(id)) return false;
            mappedCriterionIds.add(id);
            return true;
        })
    );
};

export const buildQuizPayload = ({ draft, id, timestamp, ownerAccountId, courseId, isFeedbackFeatureDisabled }) => ({
    id,
    active: true,
    is_draft: true,
    owner_account_id: ownerAccountId,
    created_on: timestamp,
    modified_on: timestamp,
    course_id: courseId,
    title: draft.quiz.title || draft.quiz.assignment?.name || '',
    questions: draft.questions.map((question) => ({
        id: question.id,
        position: question.position,
        points_possible: question.points_possible,
        ai_gradable: question.ai_gradable,
        grading_notes: question.grading_notes,
        title: question.title,
        criterion_ids:
            question.ai_gradable && draft.form.rubricType === QuizRubricType.QUESTION
                ? [...question.criterion_ids]
                : [],
    })),
    rubric: draft.form.rubric,
    rubric_type: draft.form.rubricType,
    question_mode: draft.form.questionMode,
    map_criteria_by_order: draft.form.mapCriteriaByOrder,
    ai_grading_enabled: draft.form.aiGradingEnabled,
    ai_feedback_enabled: draft.form.aiFeedbackEnabled && !isFeedbackFeatureDisabled,
    ai_feedback_settings: {
        structure: draft.form.feedbackSettings.structure,
        num_suggestions: draft.form.feedbackSettings.numSuggestions,
        suggestion_length: draft.form.feedbackSettings.suggestionLength,
        language_level: draft.form.feedbackSettings.languageLevel,
        tone: draft.form.feedbackSettings.tone,
        templated: draft.form.feedbackSettings.templated,
        custom_instructions: draft.form.feedbackSettings.customInstructions,
        feedback_examples: draft.form.feedbackSettings.feedbackExamples,
    },
    ai_feedback_context: draft.form.feedbackCustomContext,
    api_assignment_id: draft.form.api_assignment_id,
    api_data: draft.form.api_data,
});

export const buildQuizUpdatePayload = ({ draft, isFeedbackFeatureDisabled }) => {
    const payload = buildQuizPayload({
        draft,
        id: draft.quiz.id,
        timestamp: draft.quiz.modified_on,
        ownerAccountId: draft.quiz.owner_account_id,
        courseId: draft.quiz.course_id,
        isFeedbackFeatureDisabled,
    });
    const editableFields = [
        'title',
        'questions',
        'rubric',
        'rubric_type',
        'question_mode',
        'map_criteria_by_order',
        'ai_grading_enabled',
        'ai_feedback_enabled',
        'ai_feedback_settings',
        'ai_feedback_context',
        'api_assignment_id',
        'api_data',
    ];

    return Object.fromEntries(editableFields.map((field) => [field, payload[field]]));
};
